import type { BadgeRecipeProps } from '../recipes/badge.recipe';

export type StatusTone = NonNullable<BadgeRecipeProps['tone']>;

/**
 * Maps a domain status onto a badge tone in one place, so "completed" is the
 * same green on the call log, the driver profile and the invoice list.
 *
 * Add new statuses here rather than picking a tone at the call site — that is
 * what keeps the colours consistent as the API grows. Keys are lowercase with
 * hyphens; StatusBadge normalises spaces and underscores before looking up.
 */
export const statusTone: Record<string, StatusTone> = {
  // Positive, finished
  completed: 'success',
  complete: 'success',
  paid: 'success',
  active: 'success',
  online: 'success',
  released: 'success',
  approved: 'success',
  // The sales AI wrote the prospect a page.
  built: 'success',

  // In progress
  'in-progress': 'info',
  // The system is working on it, rather than a person. Purple keeps a running
  // import distinct from a fact about the record, which is what info means.
  processing: 'processing',
  // Driver states that are a job phase rather than a severity. These get the
  // phase tones so a driver reading "towing" on the dashboard sees the same
  // colour the map uses for that leg of the job.
  'en-route': 'phase-en-route',
  towing: 'phase-towing',
  returning: 'phase-returning',
  assigned: 'info',
  dispatched: 'primary',
  // A call the partner network has accepted but not yet dispatched a driver to.
  'accepted-awaiting': 'info',
  enroute: 'info',
  'on-scene': 'info',

  // Waiting on something
  //
  // `pending` is info rather than warning: the dispatch screens have shown it
  // blue since before this table existed, and a pending call is not something
  // anyone has to act on — it is simply not started. Two screens rendering the
  // same call in two colours is worse than either choice.
  pending: 'info',
  waiting: 'warning',
  // An invoice that has gone out but not been paid — awaiting someone else.
  // Also a share recipient whose link went out and has not been opened.
  sent: 'warning',
  unassigned: 'warning',
  overdue: 'warning',
  'partially-paid': 'warning',

  // Failed or stopped
  cancelled: 'destructive',
  canceled: 'destructive',
  failed: 'destructive',
  rejected: 'destructive',
  void: 'destructive',
  // A step the system gave up on, such as the sales AI after three tries.
  error: 'destructive',

  // No particular signal
  draft: 'neutral',
  clear: 'info',
  'on-leave': 'neutral',
  inactive: 'destructive',
  // A call that has come in but has not been acted on yet.
  new: 'neutral',
  offline: 'neutral',
  archived: 'neutral',
  // The sales AI judged a call not worth a page. A verdict, not a failure.
  skipped: 'neutral',
  // A share recipient who opened their link.
  viewed: 'success',
  // A share recipient whose access was taken away.
  revoked: 'destructive',
};
